import React, { useState } from "react";
import { ChevronDown, ChevronUp, Calendar, Building2, IndianRupee } from "lucide-react";
import useGetSettlementsById from "../../hooks/useGetSettlementsById";
import { formatINR, formatINRNumber } from "../../utils/formatINR";

const SETTLEMENT_STATUS = {
  FULL: 'full',
  PARTIAL: 'partial'
};

const STATUS_STYLES = {
  [SETTLEMENT_STATUS.FULL]: {
    badge: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    progress: 'bg-emerald-500'
  },
  [SETTLEMENT_STATUS.PARTIAL]: {
    badge: 'bg-amber-50 text-amber-700 border border-amber-200',
    progress: 'bg-amber-500'
  }
};

export default function SettlementItem({ settlement, isLast }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const { data: stations, isLoading, error } = useGetSettlementsById(
    isExpanded ? settlement?.id : null
  );

  // FIXED: Added total_amount_paid_for_current_month to the memoized object
  const settlementMetrics = React.useMemo(() => {
    const amountPaid = settlement?.total_amount_paid ?? 0;
    const totalDue = settlement?.total_amount_due ?? 0;
    const monthlyPaid = settlement?.total_amount_paid_for_current_month ?? 0;
    const completionRate = totalDue > 0 ? (amountPaid / totalDue) * 100 : 0;
    
    return {
      amountPaid,
      totalDue,
      monthlyPaid,
      completionRate: Math.min(Math.max(completionRate, 0), 100),
      balance: totalDue - monthlyPaid
    };
  }, [settlement]);

  const formatCurrency = (amount) => formatINR(amount || 0);

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      return new Intl.DateTimeFormat('en-IN', { 
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(new Date(dateString));
    } catch {
      return "Invalid Date";
    }
  };

  const settlementType = settlement?.settlement_type || SETTLEMENT_STATUS.PARTIAL;
  const statusStyle = STATUS_STYLES[settlementType] || STATUS_STYLES[SETTLEMENT_STATUS.PARTIAL];

  const handleToggle = () => {
    setIsExpanded(prev => !prev);
  };

  return (
    <article 
      className={`bg-white transition-all duration-200 ${
        !isLast ? 'border-b border-gray-200' : ''
      }`}
    >
      {/* Header Section */}
      <button
        onClick={handleToggle}
        className="w-full p-6 text-left hover:bg-gray-50 transition-colors duration-150 focus:outline-none focus:bg-gray-50"
        aria-expanded={isExpanded}
        aria-controls={`settlement-details-${settlement?.id}`}
      >
        <div className="flex justify-between items-start gap-6">
          {/* Left Section: Metadata */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-3">
              <h3 className="text-base font-semibold text-gray-900">
                {settlement?.remarks || `Group ${settlement?.group_id}`}
              </h3>
              <span className={`px-2.5 py-0.5 text-xs font-medium rounded-md ${statusStyle.badge}`}>
                {settlementType.toUpperCase()}
              </span>
              <span className="text-gray-400 ml-auto">
                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </span>
            </div>
            
            <dl className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar size={14} className="text-gray-400 flex-shrink-0" />
                <dt className="sr-only">Settlement Date</dt>
                <dd>
                  <span className="font-medium">Deposited on:</span>{" "}
                  {formatDate(settlement?.settlement_verification_date)}
                </dd>
              </div>
              <div className="text-xs text-gray-500 ml-6">
                Recorded: {formatDate(settlement?.created_at)}
              </div>
            </dl>
          </div>
          
          {/* Right Section: Financial Summary */}
          <div className="text-right shrink-0">
            <div className="mb-3">
              <div className="text-2xl font-bold text-gray-900 tracking-tight">
                {formatCurrency(settlementMetrics.amountPaid)}
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-40">
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2">
                <div 
                  className={`h-full transition-all duration-300 ease-out ${statusStyle.progress}`}
                  style={{ width: `${settlementMetrics.completionRate}%` }}
                  role="progressbar"
                  aria-valuenow={settlementMetrics.completionRate}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
              <div className="space-y-1">
                {settlementMetrics.balance > 0 && (
                  <div className="text-xs font-medium text-amber-600">
                    {formatCurrency(settlementMetrics.balance)} pending
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* FIXED: Using settlementMetrics.monthlyPaid instead of the undefined property */}
        <div className="mt-4 flex items-center justify-center gap-1.5 text-[14px] tracking-tight text-gray-500 border-t border-gray-100 pt-4">
            <span className="font-medium text-gray-900">
                Deposited {formatCurrency(settlementMetrics.monthlyPaid)}
            </span>
            <span className="text-gray-400">/</span>
            <span>
                from {formatCurrency(settlementMetrics.totalDue)}
            </span>
        </div>
      </button>

      {/* Expanded Details Section */}
      {isExpanded && (
        <section
          id={`settlement-details-${settlement?.id}`}
          className="px-6 pb-6 bg-gray-50 border-t border-gray-200"
        >
          <div className="pt-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Building2 size={14} />
              Station Breakdown
            </h4>

            {isLoading && (
              <div className="flex items-center justify-center gap-3 py-8">
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-gray-600">Loading station details...</span>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                Failed to load station details. Please try again.
              </div>
            )}

            {!isLoading && !error && (
              <div className="space-y-2">
                {stations && stations.length > 0 ? (
                  <ul className="space-y-2">
                    {stations.map((station, idx) => (
                      <li 
                        key={station.id || idx}
                        className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow duration-150"
                      >
                        <div className="flex justify-between items-center gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-900 text-sm mb-1">
                              {station.station_name || station.station_id}
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                              <Calendar size={12} className="flex-shrink-0" />
                              {formatDate(station.for_date)}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-semibold text-gray-900 flex items-center justify-end gap-0.5">
                              <IndianRupee size={14} />
                              <span className="tabular-nums">{formatINRNumber(station.amount_paid || 0)}</span>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-8 text-sm text-gray-500">
                    No station details available for this settlement.
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}
    </article>
  );
}